import React, {
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from 'react';

import { FormGroup, ControlLabel, FormControl } from 'react-bootstrap';
import { useHistory, useLocation } from 'react-router-dom';

import GroupedFormField from '../../../utils/FormWidgets/FormGroup';
import ActivityViewDeepEditableField from '../../ActivityView/ActivityViewDeepEditableField';
import {
  AdditionalCharges,
  BookingOption,
  BookingOptionSubType,
} from '../../../../types/Activity';
import AttendeeRequirements from '../../BookingOption/AttendeeRequirements';
import BookingOptionLocation from '../../BookingOptionLocation';
import { isAttendeesRequired } from '../../utils';
import { ActivityContext } from '../../../../contexts/Activity';
import {
  isAddonsSupported,
  isFareharborSource,
} from '../../../forms/EditPeekAndRedeamOrderForm/helpers';
import { ModalState } from '../../../../types/ModalState';

import S from './styles';
import {
  RadioBookingOptionField,
  SelectBookingOptionField,
} from './components/controls/Fields';
import FHReferenceModal from './components/FHReferenceModal/index';
import {
  FHReferenceFormChanges,
  FHReferenceFormInputProps,
} from './components/FHReferenceModal/FHReferenceForm';

type Props = {
  option: Partial<BookingOption>;
  isNotIntegrationEnabled?: boolean;
  isPriceValid: boolean;
  noMaximumAttendees: boolean;
  onChange(key: string, value: unknown): void;
  onChangeAdditionalCharges(key: string, value: unknown): void;
  onFormSubmit(event): void;
  setNoMaximumAttendees(value: boolean): void;
  setFieldsValue(fields: Partial<BookingOption>): void;
} & PropsWithChildren;

const renderBookingOptionField = (
  isNotIntegrationEnabled: boolean,
  children: React.ReactNode
) => {
  if (isNotIntegrationEnabled) {
    return null;
  }
  return children;
};

const BookingOptionContent = ({
  isNotIntegrationEnabled = false,
  children,
  option,
  isPriceValid,
  noMaximumAttendees,
  onChange,
  onChangeAdditionalCharges,
  onFormSubmit,
  setNoMaximumAttendees,
  setFieldsValue,
}: Props) => {
  const history = useHistory();
  const location = useLocation();
  const [fhReferenceModalState, setFhReferenceModalState] = useState<
    ModalState<FHReferenceFormInputProps>
  >({ visible: false });

  useEffect(() => {
    if (location.pathname.includes('fhReference')) {
      setFhReferenceModalState({
        visible: true,
        data: { itemId: option.sourceId, customerPrototypeId: option.id },
      });
    } else {
      setFhReferenceModalState({ visible: false });
    }
  }, [location.pathname]);

  const activityContext = useContext(ActivityContext);
  const onAvailableDaysChange = values => {
    onChange('hours', values[0].value);
  };
  const onSubTypeChange = (subType: BookingOptionSubType | undefined) => {
    const updates = { ...option, subType };
    updates.attendeesRequired = false;
    if (subType !== 'addon') {
      updates.attendeesRequired = isAttendeesRequired(
        updates.attendeesMinimum,
        updates.attendeesMaximum
      );
    }
    setFieldsValue(onAttendeeRequirementsHandle(updates));
  };
  const onAttendeesNumberChange = (key: string, value: unknown): void => {
    const updates = { ...option, [key]: value };
    if (subType !== 'addon' && !updates.attendeesRequired) {
      updates.attendeesRequired = isAttendeesRequired(
        updates.attendeesMinimum,
        updates.attendeesMaximum
      );
    }

    setFieldsValue(updates);
  };

  const onAttendeesRequiredChange = (key: string, value: boolean): void => {
    const updates = { ...option, [key]: value };
    setNoMaximumAttendees(!value);
    if (value) {
      updates.attendeesMaximum = 0;
    } else {
      updates.attendeesMinimum = 1;
      updates.attendeesMaximum = null;
    }
    setFieldsValue(updates);
  };

  const { subType, additionalCharges = [] } = option;

  const isAddon = subType === 'addon';

  const onPricingModelChange = (value: string) => {
    const updatedValues: Partial<BookingOption> = {
      ...option,
      pricingModel: value,
    };
    setFieldsValue(onAttendeeRequirementsHandle(updatedValues));
  };

  const onAttendeeRequirementsHandle = (values: Partial<BookingOption>) => {
    const updatedValues: Partial<BookingOption> = {
      ...values,
    };
    const isPerParty = updatedValues.pricingModel === 'party';
    if (updatedValues.subType === 'addon') {
      setNoMaximumAttendees(isPerParty);
      if (isPerParty) {
        updatedValues.attendeesMinimum = 1;
        updatedValues.attendeesMaximum = null;
      } else {
        setNoMaximumAttendees(false);
        updatedValues.attendeesMaximum = null;
      }
    }
    return updatedValues;
  };

  const renderAttendeeRequirements = () => {
    return (
      <AttendeeRequirements
        fieldsValue={option}
        noMaximumAttendees={noMaximumAttendees}
        onChange={onAttendeesNumberChange}
        setNoMaximumAttendees={setNoMaximumAttendees}
      />
    );
  };
  const renderAdditionalCharges = (item: AdditionalCharges) => {
    const { percent = 0, name } = item;
    return (
      <GroupedFormField
        key={name}
        title={name + '(as percents)'}
        placeholder={name}
        onChange={event => {
          onChangeAdditionalCharges(name, event.target.value);
        }}
        value={percent}
        type='number'
        min={0}
        max={100}
      />
    );
  };

  const handleFareharborReferenceClick = () => {
    history.push(
      `/activities/${activityContext.id}/options/${option.id}/edit/fhReference`,
      {
        skipReload: true,
      }
    );
    setFhReferenceModalState({
      visible: true,
      data: { itemId: option.sourceId, customerPrototypeId: option.id },
    });
  };
  const handleCloseFareharborReferenceModal = () => {
    history.push(
      `/activities/${activityContext.id}/options/${option.id}/edit`,
      {
        skipReload: true,
      }
    );
    setFhReferenceModalState({ visible: false });
  };

  const handleApplyFareharborReferenceChanges = (
    data: FHReferenceFormChanges
  ) => {
    setFieldsValue({
      ...option,
      sourceId: data.itemId,
      id: data.customerPrototypeId,
    });
    handleCloseFareharborReferenceModal();
  };

  return (
    <>
      <S.Wrapper onSubmit={onFormSubmit}>
        <FormGroup>
          <S.GroupedLabel>
            <ControlLabel>Title*</ControlLabel>

            {isFareharborSource(activityContext.source) && (
              <button
                type='button'
                className='btn btn-link text-right'
                onClick={handleFareharborReferenceClick}
              >
                Fareharbor reference
              </button>
            )}
          </S.GroupedLabel>
          <FormControl
            type='text'
            placeholder='Title'
            onChange={event => onChange('title', event.target.value)}
            value={option.title || ''}
          />
        </FormGroup>

        <FormGroup>
          <ControlLabel>Description</ControlLabel>
          {
            // TODO: (fix build workaround) Need to be fixed next time when this code will be changed
            // @ts-ignore
            <ActivityViewDeepEditableField
              activity={option}
              fieldsInEditMode={['description']}
              name='description'
              setParentState={value => onChange('description', value)}
            />
          }
        </FormGroup>
        <GroupedFormField
          title='Duration (in minutes)'
          placeholder='Duration'
          onChange={event => onChange('duration', +event.target.value)}
          value={option.duration || ''}
        />
        {renderBookingOptionField(
          isNotIntegrationEnabled,
          <S.AvailableDays
            label='Booking Options Hours'
            value={option.hours}
            initialValue={option.hours}
            onMetaFieldsChange={onAvailableDaysChange}
            display='block'
          />
        )}
        {renderBookingOptionField(
          isNotIntegrationEnabled,
          <GroupedFormField
            title='Interval (in minutes)'
            placeholder='Interval'
            onChange={event => onChange('interval', +event.target.value)}
            value={option.interval || ''}
          />
        )}
        <GroupedFormField
          title='Price*'
          placeholder='Price'
          onChange={event => onChange('price', event.target.value)}
          value={option.price || ''}
        />
        {option.price && !isPriceValid && (
          <S.ErrorMessage>
            {`Please input price in format: "$.$$"`}
          </S.ErrorMessage>
        )}
        <SelectBookingOptionField
          label='Pricing Model*'
          onChange={onPricingModelChange}
          value={option.pricingModel}
          options={[
            { label: 'Person', value: 'person' },
            { label: 'Party', value: 'party' },
          ]}
        />
        <SelectBookingOptionField
          label='Subtype*'
          disabled={!isAddonsSupported(activityContext.source)}
          onChange={onSubTypeChange}
          value={option.subType}
          options={[
            { label: 'Booking Option', value: 'bookingOption' },
            { label: 'Add-on', value: 'addon' },
          ]}
        />

        <RadioBookingOptionField
          label='# of guests required?'
          name='attendeesRequired'
          value={option.attendeesRequired}
          onChange={onAttendeesRequiredChange}
          options={[
            { label: 'Yes', value: true },
            { label: 'No', value: false },
          ]}
          disabled={isAddon}
        />
        <GroupedFormField
          title='Tax Rate (in percents)*'
          placeholder='Tax Rate'
          onChange={event => onChange('taxRate', event.target.value)}
          value={option.taxRate || ''}
          min={0}
          type='number'
          max={100}
        />
        <BookingOptionLocation
          bookingOption={option}
          onChange={setFieldsValue}
        />
        {renderAttendeeRequirements()}
        {!isAddon && additionalCharges.map(renderAdditionalCharges)}
        {children}
      </S.Wrapper>
      {fhReferenceModalState.visible && (
        <FHReferenceModal
          onClose={handleCloseFareharborReferenceModal}
          onApplyChanges={handleApplyFareharborReferenceChanges}
          {...fhReferenceModalState.data}
        />
      )}
    </>
  );
};

export default BookingOptionContent;
